import React, { useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import {
    composeFileName,
    downloadFile,
    isEmptyList,
    isFiltersTimeRangeExceededForExport
} from '../../../utils'
import { Loading, PageTitle } from '../../root/components/common'
import { ReportsTable } from '../../root/components/common/reports-table/reports-table'
import { useReportsFilters } from '../../root/hooks/useReportsFilters'
import {
    urlToSearchState,
    useSyncUrlState
} from '../../../utils/hooks/useSyncUrlState'
import * as actions from '../actions'
import { useLocation } from 'react-router-dom'
import { ReportsFilters } from '../../root/components/common/reports-filters'
import { UserPermissions } from 'web/src/services/permissions'
import { OptionSwitcher } from '../../root/components/common/option-switcher/option-switcher'
import { LOCAL_LOCALE_KEY, REPRESENTATION } from '../../root/constants'
import { getFileForExport } from '../../reports/api/report-api'
import { enqueueSnackbar } from '../../root/actions'
import { ExportMenu } from '../../root/components/common/export-menu'

const useStyles = makeStyles(theme => ({
    deviceReportsWrapper: {
        height: 'calc(100vh - 215px)',
        marginTop: 20,
        marginBottom: 20
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 24
    },
    title: {
        flexGrow: 1,
        marginBottom: 0,
        width: 200
    },

    pageTitleAssignedIcon: {
        marginLeft: 4
    },
    switcher: {
        marginRight: 12
    },
    exportBtnsWrapper: {
        display: 'flex',
        gap: '6px',
        marginRight: '16px'
    },
    loader: {
        top: '50vh',
        zIndex: 9,
        position: 'absolute',
        left: 0,
        right: 0,
        marginLeft: 'auto',
        marginRight: 'auto'
    }
}))

export function DeviceReports({ device }) {
    const classes = useStyles()
    const fromShakeableRef = useRef(null)
    const { locale, isLoading } = useSelector(state => state.main)
    const dispatch = useDispatch()

    const { deviceReports } = useSelector(state => state.devices)

    const location = useLocation()
    const { filters, d, isLoadingFilters } = useReportsFilters(
        urlToSearchState(location),
        UserPermissions.isAllOriginalGranted(),
        device.id,
        null
    )
    const [isExporting, setIsExporting] = useState(false)

    const exportReports = async fileType => {
        if (isFiltersTimeRangeExceededForExport(filters)) {
            fromShakeableRef.current.shake()
            dispatch(
                enqueueSnackbar({
                    message: locale.reports.exportDenyMsg,
                    options: {
                        variant: 'warning'
                    }
                })
            )
            return
        }
        setIsExporting(true)
        const locale_key = localStorage.getItem(LOCAL_LOCALE_KEY)
        const params = {
            fileType,
            locale_key,
            deviceId: device.id,
            deviceSerial: device.serial_number,
            ...filters
        }
        const blob = await getFileForExport(params)
        if (blob) {
            const fileName = composeFileName('reports', fileType)
            downloadFile(blob, fileType, fileName)
        } else {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.getDeviceReportsEx,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
        setIsExporting(false)
    }

    useSyncUrlState(filters)

    useEffect(() => {
        if (!isLoadingFilters) {
            dispatch(actions.deviceReportsRequest(device.id, filters))
        }
    }, [filters, isLoadingFilters])

    if (!deviceReports?.length && isLoading) {
        return <Loading />
    }

    return (
        <>
            {isExporting && (
                <div className={classes.loader}>
                    <Loading />
                </div>
            )}
            <div className={classes.deviceReportsWrapper}>
                <div className={classes.header}>
                    <PageTitle
                        className={classes.title}
                        title={
                            <div className={classes.pageTitle}>
                                {`${device.name} ${locale.reports.reports}`}
                            </div>
                        }
                    />
                    <div className={classes.exportBtnsWrapper}>
                        <ExportMenu>
                            {({ handleClose }) => (
                                <>
                                    <ExportMenu.Csv
                                        locale={locale}
                                        onExport={fileType => {
                                            exportReports(fileType)
                                            handleClose()
                                        }}
                                    />
                                    <ExportMenu.Excel
                                        locale={locale}
                                        onExport={fileType => {
                                            exportReports(fileType)
                                            handleClose()
                                        }}
                                    />
                                    <ExportMenu.Pdf
                                        locale={locale}
                                        onExport={fileType => {
                                            exportReports(fileType)
                                            handleClose()
                                        }}
                                    />
                                </>
                            )}
                        </ExportMenu>
                    </div>
                    {((UserPermissions.isAllOriginalGranted() &&
                        UserPermissions.isOriginalEntity(
                            device?.customer_id
                        )) ||
                        UserPermissions.isTraplinkedAdmin()) && (
                        <OptionSwitcher
                            className={classes.switcher}
                            options={{
                                [REPRESENTATION.all]: locale.reports.allReports,
                                [REPRESENTATION.assigned]:
                                    locale.devices.assigned
                            }}
                            selected={
                                filters.is_assigned
                                    ? REPRESENTATION.assigned
                                    : REPRESENTATION.all
                            }
                            onChange={value =>
                                d({
                                    type: 'is_assigned',
                                    value: value === REPRESENTATION.assigned
                                })
                            }
                        />
                    )}
                    <ReportsFilters
                        onChange={d}
                        filters={filters}
                        fromShakeableRef={fromShakeableRef}
                    />
                </div>
                {!isEmptyList(deviceReports) && (
                    <ReportsTable
                        isLoading={isLoading}
                        sorting={filters.sort_by}
                        onSort={sort_by => d({ type: 'sort', sort_by })}
                        contextPage="device"
                        reports={deviceReports}
                    />
                )}
                {isEmptyList(deviceReports) && (
                    <div>
                        {locale.devices.noReportsForRange} {device.name}
                    </div>
                )}
            </div>
        </>
    )
}
